import { DocumentType } from '../../@types/enumsGlobal'

export interface KizControlContext {
    requiredMode: boolean
    validationType?: string
};

export const countKizSum = (
    param: any,
    documentType?: DocumentType,
    isUnpackRecursive?: boolean,
    trustedSupplier?: boolean
): number => {

    const isDisaggregation = documentType === DocumentType.disaggregation;
    const isInvoiceOrImport =
        documentType === DocumentType.invoice ||
        documentType === DocumentType.importRemains;

    if (isDisaggregation) {
        const confirmationScannedNo = (param.kizConfirmationScanned?.filter(x => x.infoScanned && x.infoScanned.length > 0).length ?? 0);
        return trustedSupplier ?
            (isUnpackRecursive ? param.kizs?.length ?? 0 : param.kizBoxes?.length ?? 0) :
            confirmationScannedNo;
    }

    if (isInvoiceOrImport) {
        const kizsCount = param.kizs?.length ?? 0;
        const boxesQuantity = param.kizBoxes?.reduce((sum: number, box: any) => sum + (box.quantity ?? 0), 0) ?? 0;
        const kizOsuDtosCount = param.kizOsuDtos?.length ?? 0;
        return kizsCount + boxesQuantity + kizOsuDtosCount;
    }

    // Базовый вариант подсчёта
    return (param.kizs?.length ?? 0) + (param.kizBoxes?.length ?? 0) +  (param.kizOsuDtos?.length ?? 0);
};

export const controlKiz = (items, showMessage: () => void, documentType?: DocumentType, controlContext?: KizControlContext): boolean => {
    
    let result: boolean = true;
    for (const x of items) {
        const sumKizsKizBoxesQuantitySum: number =  (x.kizs?.length ?? 0) + 
            (x.kizBoxes ? x.kizBoxes.reduce((currentSum, kizBox) => currentSum + kizBox.quantity, 0) : 0) +
            (x.kizBoxesGisMt ? x.kizBoxesGisMt.reduce((currentSum, kizBoxesGisMt) => currentSum + kizBoxesGisMt.quantity, 0) : 0) +
            (x.kizOsuDtos?.length ?? 0);

        const sumKizsKizBoxes: number = (x?.kizs?.length ?? 0) + (x?.kizBoxes?.length ?? 0) + (x?.kizOsuDtos?.length ?? 0);        
        const quantity: number = x.quantity ?? 0;
        const actualQuantity: number = x.actualQuantity ?? 0;
        const numerator: number = x.numerator ?? 0;
        const denominator: number = x.denominator ?? 0;
        const isKizOsu: boolean = x.isOsu ?? false;
        
        if (x.isKiz || x.isKizBox) {
            // Приходная накладная
            if (documentType === DocumentType.invoice) {
                if (numerator < denominator) {
                    if ((sumKizsKizBoxesQuantitySum !== 1) && !isKizOsu) {
                        result = false;
                        break;
                    }
                } else {
                    if (controlContext && controlContext.validationType === 'ActualQuantity') {
                        if ((sumKizsKizBoxesQuantitySum < actualQuantity) && !isKizOsu) {
                            result = false;
                            break;
                        }
                    } else {
                        if ((sumKizsKizBoxesQuantitySum < quantity) && !isKizOsu) {
                            result = false;
                            break;
                        }
                    }
                }
            }

            // Ввод остатков
            if (documentType === DocumentType.importRemains) {
                if (numerator < denominator) {
                    if ((sumKizsKizBoxesQuantitySum !== 1) && !isKizOsu) {
                        result = false;
                        break;
                    }
                } else {
                    if ((sumKizsKizBoxesQuantitySum < quantity) && !isKizOsu) {
                        result = false;
                        break;
                    }
                }
            }

            // Акт разукомплектации
            if (documentType === DocumentType.disassembling) {
                if ((sumKizsKizBoxes !== x.quantityFrom)) {
                    result = false;
                    break;
                }
            }

            // Акт разагрегации
            if (documentType === DocumentType.disaggregation) {
                if (x.kizBoxes?.length === 0 && x.kizs?.length !== quantity) {
                    result = false;
                    break;
                }

                // Разагрегация до коробов
                if (x.kizBoxes?.length !== quantity && x.kizs?.length === 0) {
                    result = false;
                    break;
                }
            }

            // Акт списания, Перемещение
            if ((documentType === DocumentType.actDeduction) || (documentType === DocumentType.movement)) {
                if (!controlContext || (controlContext && controlContext.requiredMode)) {
                    if (numerator < denominator) {
                        if (sumKizsKizBoxesQuantitySum !== 1) {
                            result = false;
                            break;
                        }
                    } else {
                        if ((sumKizsKizBoxes !== quantity)) {
                            result = false;
                            break;
                        }
                    }
                }
                else {
                    result = true;
                    break;
                }
            }

            // Инвентаризационная ведомость
            if (documentType === DocumentType.inventoryVed) {
                const isDivided = numerator < denominator;
                const kizScanned = x.countKizScan ?? 0;
                const isRequiredMode = !controlContext || controlContext.requiredMode;
                const documentalQuantity = x.documentalQuantity;

                // Правила валидации для разных сценариев
                const isValid = (() => {
                    // Обязательный режим сканирования
                    if (isRequiredMode) {
                        if (isDivided) return kizScanned === 1;
                        if (!x.isOsu) return kizScanned === quantity;
                        return true; // ОСУ всегда проходит
                    }

                    // Необязательный режим сканирования
                    // Дробные или ОСУ всегда проходят
                    if (isDivided || x.isOsu) return true;

                    // Для целых не-ОСУ: либо документальное совпадает, либо всё отсканировано
                    return documentalQuantity === quantity || kizScanned === quantity;
                })();

                if (!isValid) {
                    result = false;
                    break;
                }
            }

            // Расходная накладная, Акт возврата поставщику, Акт переоценки
            if (documentType === DocumentType.invoiceOut || documentType === DocumentType.actReturnToContractor || documentType === DocumentType.revaluation) {
                if (!controlContext || (controlContext && controlContext.requiredMode)) {
                    if ((sumKizsKizBoxes !== quantity)) {
                        result = false;
                        break;
                    }
                }
                else {
                    result = true;
                    break;
                }
            }
        }
    }

    if (!result) {
        showMessage()
    }
    
    return result
}
